<template>
  <div>
    <div class="checklist-builder__second-stage-title">
      {{ $t('Invite users') }}

    </div>

    <div class="text-sm text-70 mb-4">
      {{ $t('feature-explanation.invite-users') }}
    </div>

    <div class="flex space-between">
      <ChipField
        ref="emails"
        class="flex-1"
        :validation-rule="emailRegEx"
        :invalid-values="invalidValues"
        @update="addEmails"
        @values-added="checkUserExistence"
        @value-deleted="deleteValue"
      />

      <button
        type="button"
        class="btn btn-default btn-secondary"
        :disabled="disabledButton"
        @click="inviteUsers"
      >
        <span v-if="!showLoader">{{ $t('Invite') }}</span>
        <loader v-else />
      </button>
    </div>
    <ul v-if="invalidValues.length" class="mt-2">
      <li v-for="(value, index) in invalidValues" :key="index" class="text-red">{{ value.error }}</li>
    </ul>
    <InviteUsersLimitModal
        v-if="showInviteModal"
        :user="loggedInUser()"
        :users-to-add="usersToAdd"
        :total-users="totalUsersCount"
        :message="freePlanMessage"
        @close="closeInviteModal"
        @updated="inviteUsers"
    />

  </div>
</template>

<script>
  import _ from 'lodash';
  import { eventBus } from '@/_legacy/eventBus';
  import ChipField from '@/_legacy/components/Controls/ChipField.vue';
  import authMixin from '@/_legacy/mixins/authMixin';

  import { useTask } from '@/ui/concerns';
  import { emailCheck, inviteUsers } from '@/services/api/resources/user';
  import { list } from '@/services/api/resources/assignee';
  import InviteUsersLimitModal from '@/_legacy/components/Modals/InviteUsersLimitModal';
  import { EventTypes, EVENTS, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
  import IconInfoCircle from '@/_legacy/components/Icons/IconInfoCircle';
  import { StatusCodes } from 'http-status-codes';
  import { v4 as uuidv4 } from 'uuid';
  import { fetchUser } from '@/ui/auth/auth';
  import { Userflow, UserflowTrackEvent } from '@/services/userflow/userflow';

  export default {
    name: 'InviteUsers',

    components: {
      ChipField,
      InviteUsersLimitModal,
      IconInfoCircle,
    },

    mixins: [authMixin],

    data() {
      return {
        emailRegEx: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        invite: [],
        loadingStack: {
          checkUserEmail: false,
          inviteUsers: false,
        },
        showInviteModal: false,
      };
    },

    computed: {
      userQuota() {
        return this.findQuota('user');
      },
      shouldDisplayModal() {
        return this.hasQuota('user') && this.getQuota('user') === 0;
      },
      totalUsersCount() {
        return this.hasQuota('user') && this.userQuota.current + (this.invite.length - this.invalidValues.length);
      },
      usersToAdd() {
        return this.hasQuota('user') && (this.totalUsersCount - this.userQuota.max);
      },
      invalidValues() {
        return _.filter(this.invite, email => !email.valid);
      },
      freePlanMessage() {
        if (!this.hasFeature('extendUserQuota')) {
          return {
            limit: this.userQuota.max,
            text: this.$t('You reached your user limit'),
            advice: this.$t('Upgrade now to create as many users as you want'),
          };
        }
        return {
          limit: this.userQuota.max,
          text: this.$t('You reached your user limit of {value}', { value: this.userQuota.max }),
          advice: this.$t('Proceeding will add {value} user seats to your account.', { value: this.usersToAdd }),
        };
      },
      disabledButton() {
        return this.invalidValues.length > 0 || this.invite.length === 0 || this.loadingStack.checkUserEmail || this.loadingStack.inviteUsers;
      },
      showLoader() {
        return this.loadingStack.inviteUsers;
      }
    },

    methods: {
      deleteValue(e) {
        const value = e.value || e[0].value;
        const index = _.findIndex(this.invite, email => email.value === value);
        if (index > -1) {
          this.invite.splice(index);
        }
      },
      checkUserExistence(email) {
        const emailValue = email.value || email[0].value;

        if (this.emailRegEx.test(emailValue)) {
          this.loadingStack.checkUserEmail = true;
          const getRecord = useTask(
            () => emailCheck({ email: emailValue }),
            () => {
              this.loadingStack.checkUserEmail = false;
            },
            uuidv4(),
            error => {
              this.loadingStack.checkUserEmail = false;
              if (error.code !== StatusCodes.OK) {
                const index = _.findIndex(this.invite, value => value.value === emailValue);
                if (index > -1) {
                  this.invite[index].valid = false;
                  this.invite[index].error = this.$t('{value} is already taken.', { value: emailValue });
                }
              }
            },
          );
          getRecord.run();
        }
      },
      addEmails(e) {
        e.map(email => {
          if (_.findIndex(this.invite, value => value.value === email.value) === -1) {
            if (!email.valid) {
              email.error = this.$t('{value} is invalid email address.', { value: email.value });
            }
            return this.invite.push(email);
          }
        });
      },
      async inviteUsers() {
        await fetchUser()();
        if (this.shouldDisplayModal) {
          return this.showUsersModal();
        }

        if (this.hasQuota('user') && this.getQuota('user') === 0) {
          if (this.hasFeature('extendUserQuota')) {
            eventBus.$emit('add-users', this.$t('You need to buy more seats to invite more users.'));
          } else {
            eventBus.$emit('free-plan-limit', { message: this.freePlanMessage });
          }
          return;
        }

        const emails = _.map(_.filter(this.invite, email => email.valid), email => email.value);
        if (!emails.length) {
          return;
        }

        trackEvent(EventTypes.inviteUsers, EVENTS.INVITE_USERS, { value: emails.length });

        this.loadingStack.inviteUsers = true;

        const getRecord = useTask(
          () => inviteUsers(emails),
          () => {
            this.$emit('reset');
            this.invite = [];
            this.$toasted.show(
              this.$t('Successfully invited user(s)!'),
              { type: 'success' },
            );
            this.loadingStack.inviteUsers = false;
            Userflow.trackEvent(UserflowTrackEvent.USER_INVITED);
            this.$nextTick(() => this.fetchUsers());
          },
          uuidv4(),
          () => {
            this.loadingStack.inviteUsers = false;
          },
        );
        getRecord.run();
      },

      async fetchUsers() {
        const getRecord = useTask(
          () => list({ page: 1, perPage: 1000 }),
          response => {
            this.$store.commit('checklist_builder/setUsers', response ? response.data : []);
          },
        );
        getRecord.run();
      },

      showUsersModal() {
        if (!this.isOnFreePlan) {
          this.showInviteModal = true;
        } else {
          eventBus.$emit('free-plan-limit', { message: this.freePlanMessage });
        }
      },

      closeInviteModal() {
        this.showInviteModal = false;
      },

    },
  };
</script>
